import { computed, effect, inject, Injectable, untracked } from '@angular/core';
import { ZIMCallUserInfo } from 'zego-zim-web';

import { LinkedOaStore } from '@app/pages/chat/linked-oa.store';

import { DeviceStore } from '../../../pages/chat/device.store';
import { UserStore } from '../../../pages/chat/user.store';
import { ZimService } from '../../../services/zim.service';
import { createLogger } from '../../../utils/logger.util';
import { isDevice } from '../../../utils/utils';
import { CallParticipant, PayloadDict } from '../types/call';
import { CallSessionStoreService } from './call-session-store.service';

@Injectable({
  providedIn: 'root',
})
export class CallUserResolverService {
  private readonly logger = createLogger('Call - CallUserResolverService');
  private userStore = inject(UserStore);
  private linkedOaStore = inject(LinkedOaStore);
  private sessionStore = inject(CallSessionStoreService);
  private zimService = inject(ZimService);
  private deviceStore = inject(DeviceStore);

  private remoteUserId = computed(() => {
    const pending = this.sessionStore.pendingInvitation();
    if (pending) return pending.inviter;
    return null;
  });

  private remoteParticipant = computed(() => {
    const pending = this.sessionStore.pendingInvitation();
    if (pending) return pending.invitees?.[0];
    const current = this.sessionStore.currentCall();
    if (current) return this.sessionStore.participants()?.[0];
    return null;
  });

  isInContact = computed(() => {
    const userId = this.remoteUserId();
    if (!userId) return false;

    return !!this.deviceStore.getDeviceByZegoId(userId) || this.userStore.hasContact(userId);
  });

  callerName = computed(() => {
    return this.remoteParticipant()?.userName || '';
  });

  callerAvatar = computed(() => {
    return this.remoteParticipant()?.avatar || '';
  });

  constructor() {
    effect(() => {
      const userId = this.remoteUserId();
      const isInContact = this.isInContact();
      if (userId && !isInContact && !isDevice(userId)) {
        untracked(() => {
          this.resolveInviter(userId);
        });
      }
    });
  }

  private async resolveInviter(userId: string) {
    try {
      const inviterPhone = await this.userStore.getPhoneNumber(userId, true);
      if (inviterPhone) {
        this.setUserName(userId, inviterPhone);
      }
    } catch (e) {
      this.logger.error('resolveInviter', e);
    }
  }

  setUserName(userId: string, inviterPhone: string) {
    // Cập nhật tên người gọi
    const pending = this.sessionStore.pendingInvitation();
    if (pending && pending.inviter === userId) {
      this.sessionStore.setPendingInvitation({
        ...pending,
        invitees: pending.invitees.map((invitee) =>
          invitee.userId === userId ? { ...invitee, userName: inviterPhone } : invitee,
        ),
      });
    }
  }

  async resolveParticipants(
    callUserList: ZIMCallUserInfo[],
    extendedData: PayloadDict,
    createTo: 'Received' | 'Created',
  ): Promise<CallParticipant[]> {
    const listCallUser = callUserList.filter((cu) => cu.userID !== this.zimService.userId);
    const listCallParticipant: CallParticipant[] = [];

    try {
      if (extendedData.oaUserId) {
        await this.linkedOaStore.fetchAndCacheLinkedOa(extendedData.oaUserId);
      }
    } catch (error) {
      this.logger.warn('userInfoList', error);
    }

    listCallUser.forEach((callUser: ZIMCallUserInfo) => {
      const user = this.userStore.getUser(callUser.userID);

      const userName = user?.isContact
        ? user?.contactName || user?.userName || user?.phoneNumber || callUser.userID
        : user?.userName || user?.phoneNumber || callUser.userID;

      listCallParticipant.push({
        userId: callUser.userID,
        userName,
        avatar: user?.userAvatarUrl || '',
        isMicOn: true,
        isCamOn: false,
        soundLevel: 0,
        state: callUser.state,
      });
    });
    return listCallParticipant;
  }
}
